import { useTheme } from "@emotion/react";
import { TextField, Box } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { tokens } from "../../theme";
import { useEffect, useState } from "react";
import Loader from "../global/Loader";
import { pageSizeOptions, pageSize, page } from "../../utils/paginationConfig";
import { DataGrid} from "@mui/x-data-grid";
import { CustomGridToolbar } from "../../utils/dataGridCustom";
import { getCenterLoginLogoutReport } from "../../services/staff-services/centerService";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import { SetCurrentPageTopic } from "../../store/action/headerChange";
import { DemoContainer, DemoItem } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import { getAllCenters } from "../../services/getCenterAttendanceDetails";
import { saveAs } from "file-saver";

const ReportLogInLogout = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const dispatch = useDispatch();
  dispatch(SetCurrentPageTopic("Center Daily Login / Logout Report"));

  const userRole = localStorage.getItem("Role");
  const userCenterCode = localStorage.getItem("CenterCode");

  const [paginationModel, setPaginationModel] = useState({page: page, pageSize: pageSize});
  const [rowCount, setRowCount] = useState(0);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [centerCode, setCenterCode] = useState("");
  const [allCenters, setAllCenters] = useState([]);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [dateRange, setDateRange] = useState([dayjs(""), dayjs("")]);
  const [dateRangeFormatted, setDateRangeFormatted] = useState(["", ""]);
  const today = dayjs();

  const headerData = ["Date", "Open Time", "Close Time", "Worked Hrs", "Student Count", "PC Count", "PC Performance"];

  const columns = [
    {
      field: "attendanceDate",
      headerName: headerData[0],
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ textTransform: "uppercase" }}>
          {params.value}
        </Box>
      ),
    },
    {
      field: "openedTime",
      headerName: headerData[1],
      flex: 1,
      cellClassName: "name-column--cell",
      renderCell: (params) => {
        const timeOnly = params.value ? params.value.slice(11, 16) : "";
        return <span>{timeOnly}</span>;
      },
    },
    {
      field: "closedTime",
      headerName: headerData[2],
      flex: 1,
      cellClassName: "name-column--cell",
      renderCell: (params) => {
        const timeOnly = params.value ? params.value.slice(11, 16) : "";
        return <span>{timeOnly}</span>;
      },
    },
    {
      field: "workedHrs",
      headerName: headerData[3],
      flex: 1,
      cellClassName: "name-column--cell",
    },
    {
      field: "studentCount",
      headerName: headerData[4],
      flex: 1,
      cellClassName: "name-column--cell",
    },
    {
      field: "pcCount",
      headerName: headerData[5],
      flex: 1,
      cellClassName: "name-column--cell",
    },
    {
      field: "pcPerformance",
      headerName: headerData[6],
      flex: 1,
      cellClassName: "name-column--cell",
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      const allCentersListResponse = await getAllCenters();

      if(userRole === "ADM" || userRole === "DPS" || userRole === "hmw") {
        const firstCenter = allCentersListResponse.data[0];
        setAllCenters(allCentersListResponse.data);
        setCenterCode(firstCenter?.centerCode);
        setSelectedCenter(firstCenter);
        await handleTableData(paginationModel.page, paginationModel.pageSize, firstCenter?.centerCode);
      }else {
        const filteredCenters = allCentersListResponse.data.filter(
          (center) => center.centerCode === userCenterCode
        );

        if(filteredCenters.length > 0) {
          const firstCenter = filteredCenters[0];
          setAllCenters(filteredCenters);
          setCenterCode(firstCenter?.centerCode);
          setSelectedCenter(firstCenter);
          await handleTableData(paginationModel.page, paginationModel.pageSize, firstCenter?.centerCode);
        }
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (dateRange[0] != null && dateRange[1] != null) {
      const startDateString = dateRange[0].$d;
      const endDateString = dateRange[1].$d;

      if (
        startDateString != "Invalid Date" &&
        endDateString != "Invalid Date"
      ) {
        const startDateObject = new Date(startDateString);
        const startMonth = startDateObject.toLocaleString("default", {
          month: "short",
        });
        const startDay = startDateObject.getDate();
        const startYear = startDateObject.getFullYear();

        const endDateObject = new Date(endDateString);
        const endMonth = endDateObject.toLocaleString("default", {
          month: "short",
        });
        const endDay = endDateObject.getDate();
        const endYear = endDateObject.getFullYear();

        const fromDateFormatted = `${startDay}-${startMonth}-${startYear}`;
        const toDateFormatted = `${endDay}-${endMonth}-${endYear}`;
        setDateRangeFormatted([fromDateFormatted, toDateFormatted]);
        handleTableData(page, pageSize, centerCode, fromDateFormatted, toDateFormatted);
      }
    }
  }, [dateRange]);

  const handleTableData = async (
    page = paginationModel.page,
    limit = paginationModel.pageSize,
    code = centerCode,
    fromDate = "",
    toDate = "",
  ) => {
    setIsLoading(true);

    setPaginationModel({page: page, pageSize: limit});

    try {
      const obj = {
        page,
        limit,
        centerCode: code,
        fromDate,
        toDate,
      };

      const response = await getCenterLoginLogoutReport(obj);
      setData(response.data.data);
      setRowCount(response.data.totalCount ?? 0);
    } catch (error) {
      console.log(error);
      toast.error("Please refresh");
    }
    setIsLoading(false);
  };

  const handleSelectCenter = (selectedOption) => {
    setSelectedCenter(selectedOption);

    if (selectedOption) {
        setCenterCode(selectedOption.centerCode);
        handleTableData(page, pageSize, selectedOption.centerCode, dateRangeFormatted[0], dateRangeFormatted[1]);
    }
 };

 const handlePaginationModelChange = (newModel) => {
   const resetPage = newModel.pageSize !== paginationModel.pageSize;
   const updatedModel = {
     ...newModel,
     page: resetPage ? 0 : newModel.page,
   };

   setPaginationModel(updatedModel);
   handleTableData(updatedModel.page, updatedModel.pageSize, centerCode, dateRangeFormatted[0], dateRangeFormatted[1]);
 };

 const handleExportCSV = async () => {
  try {
    const response = await getCenterLoginLogoutReport({
      page: 0,
      limit: rowCount,
      centerCode: centerCode,
      fromDate: dateRangeFormatted[0],
      toDate: dateRangeFormatted[1],
    });

    const data = response.data.data;
    const csvRows = data.map(row =>
      [
        row.attendanceDate,
        row.openedTime?.substring(11, 16),
        row.closedTime?.substring(11, 16),
        row.workedHrs,
        row.studentCount,
        row.pcCount,
        row.pcPerformance,
      ].join(",")
    );

    const csvContent = [headerData.join(","), ...csvRows].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `Center_login_logout_report.csv`);
  } catch (error) {
    console.error("CSV Export Failed:", error);
    toast.error("Failed to export CSV.");
  }

 };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Box m="0 20px">
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box sx={{ flex: 3, mt: "6px", mr: "10px" }}>
          <Autocomplete
            options={allCenters}
            getOptionLabel={(option) => option.centerName || ""}
            value={selectedCenter}
            onChange={(event, value) => handleSelectCenter(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Center"
                variant="filled"
                fullWidth
              />
            )}
          />
        </Box>
        <Box sx={{ flex: 4 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer components={["DateRangePicker", "DateRangePicker"]}>
              <DemoItem component="DateRangePicker">
                <DateRangePicker
                  value={dateRange}
                  onChange={(newValue) => setDateRange(newValue)}
                  defaultValue={today}
                  disableFuture
                  minDate={dayjs("2024-01-01")}
                  maxDate={dayjs().subtract(1, "day")}
                  slotProps={{
                    textField: {
                      variant: "filled",
                      fullWidth: true,
                    },
                  }}
                />
              </DemoItem>
            </DemoContainer>
          </LocalizationProvider>
        </Box>
        <Box sx={{ flex: 2, mt: "6px", ml: "10px" }}></Box>
      </Box>

      <Box
        display="grid"
        width="100%"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
            fontSize: "15px",
          },
          "& .name-column--cell": {
            color: colors.primary[100],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            border: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.redAccent[100]} !important`,
          },
        }}
      >
        <DataGrid
          rows={data}
          getRowId={(row) => row.cenAttendanceCode}
          columns={columns}
          paginationMode="server"
          rowCount={rowCount}
          pageSizeOptions={pageSizeOptions}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          loading={isLoading}
          components={{ Toolbar: CustomGridToolbar }}
          componentsProps={{
            toolbar: {
              onExport: handleExportCSV,
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default ReportLogInLogout;
